// src/components/dashboard/StatisticsPanel.tsx
import React, { useState } from "react";
import {
    Typography,
    Box,
    Paper,
    Divider,
    Grid,
    Tabs,
    Tab,
    Badge
} from "@mui/material";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BarChartIcon from "@mui/icons-material/BarChart";
import GroupIcon from "@mui/icons-material/Group";
import MissingExercisesStats from "./MissingExercisesStats";
import DeregisteredAthletesStats from "./DeregisteredAthletesStats";
import FinancialOverviewPanel from "./FinancialOverviewPanel";
import ClubOverviewStats from "./ClubOverviewStats";

interface StatisticsPanelProps {
    competitionId: string;
}

// TabPanel Komponente
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`statistics-tabpanel-${index}`}
            aria-labelledby={`statistics-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `statistics-tab-${index}`,
        'aria-controls': `statistics-tabpanel-${index}`,
    };
}

const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ competitionId }) => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Wettkampf-Statistiken
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Umfassende Übersicht zu wichtigen Kennzahlen, Finanzen und Status-Informationen des Wettkampfs.
            </Typography>
            
            {/* Tab Navigation */}
            <Paper elevation={1} sx={{ mb: 3 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="statistics tabs"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab 
                        icon={<BarChartIcon />}
                        label="Wettkampfkarten" 
                        {...a11yProps(0)} 
                        iconPosition="start"
                        sx={{ minHeight: 72 }}
                    />
                    <Tab 
                        icon={<AccountBalanceIcon />}
                        label="Finanzübersicht" 
                        {...a11yProps(1)} 
                        iconPosition="start"
                        sx={{ minHeight: 72 }}
                    />
                    <Tab
                        icon={<GroupIcon />}
                        label="Vereinsübersicht"
                        {...a11yProps(2)}
                        iconPosition="start"
                        sx={{ minHeight: 72 }}
                    />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <TabPanel value={tabValue} index={0}>
                {/* Allgemeine Statistiken */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                color: 'error.main'
                            }}>
                                <AssignmentLateIcon />
                                Fehlende Pflichtübungen / Wettkampfkarten
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <MissingExercisesStats competitionId={competitionId} />
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <DeregisteredAthletesStats competitionId={competitionId} />
                        </Paper>
                    </Grid>
                    
                    {/* Platz für weitere allgemeine Statistiken */}
                    {/* 
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Meldestatus nach Wettkampfklassen
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ p: 2 }}>
                                [Visualisierung des Meldestatus]
                            </Box>
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Wettkampfkarten-Status
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ p: 2 }}>
                                [Übersicht über eingereichte Wettkampfkarten]
                            </Box>
                        </Paper>
                    </Grid>
                    */}
                </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {/* Finanzübersicht */}
                <FinancialOverviewPanel competitionId={competitionId} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <ClubOverviewStats competitionId={competitionId} />
            </TabPanel>
        </Box>
    );
};

export default StatisticsPanel;